/**
 * V2EstimateDetailPage — the flagship master–detail "Estimate detail" screen.
 *
 * Left: a {@link TreeRail} of areas (rooms) + line-item search + pinned
 * summaries. Right: a dense, surface-grouped line-item grid with inline
 * CAT/SEL editing, activity symbols, confidence pills, source bands, and an
 * inline "Why this code?" expander.
 *
 * DATA TRUTHS (load-bearing):
 *  - `ScopeResponseLineItem` has NO price/total field. "Unit price" and
 *    "Total" render as "—"; the header/area rollups show COUNTS, not money.
 *    Pricing is computed in Xactimate on export (footer note states this).
 *  - `xactimate_code` is ONE string → split with `splitXactimateCode` for the
 *    CAT/SEL cells; `joinXactimateCode` on commit.
 *  - Grouping/ordering uses the IMPORTED `groupItemsBySection` from
 *    ScopeListView.tsx (group by surface, order by sequence) — not reinvented.
 *  - Inline CAT/SEL edits are SESSION-LOCAL: held in an override map, never
 *    sent to an endpoint (no per-line scope PATCH exists). See the seam below.
 *
 * PRP-WEB-1 W2.3-4: the page opens on Overview (ProjectOverview: estimate cards, plan
 * thumbnail, claim facts); Floor plan, Intake and Notes & photos are tabs; this grid is the
 * Scope tab, shown only when a field-scope row exists. "Estimates" in the breadcrumb is a link
 * to the dashboard, and Back goes there when there is no in-app history.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import useProjectStore from '../../stores/projectStore';
import { BackendAPI } from '../../lib/api';
import { useScopeEsxExport } from '../../hooks/useScopeEsxExport';
import type { Project, ScopeResponseRoom, ScopeResponseLineItem, FieldNote } from '../../types';
import { groupItemsBySection } from '../../components/scope/ScopeListView';
import { splitXactimateCode, joinXactimateCode } from '../lib/xcode';
import { activitySymbol, ACT_LABEL, sourceMeta, SOURCE_META, CONF_META } from '../lib/semantics';
import { TreeRail, SEL_ALL, SEL_REVIEW } from '../components/TreeRail';
import { RoofScopeSection } from '../components/roof/RoofScopeSection';
import { FloorPlanView } from '../components/FloorPlanView';
import { projectLabel } from '../components/EstimateCard';
import ProjectOverview, { useEstimateDeletes } from '../components/ProjectOverview';
import {
  IntakeTabPanel,
  NotesTabPanel,
  PageTabs,
  PlanTabPanel,
  useProjectPageTab,
} from '../components/ProjectPageTabs';
import { CodeCell } from '../components/CodeCell';
import { Button } from '../components/ui/button';
import { Pill } from '../components/ui/pill';
import { EmptyState } from '../components/ui/empty-state';
import { ProjectDetailsModal } from '../components/ProjectDetailsModal';
import {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '../components/ui/tooltip';
import {
  IconChevl,
  IconDollar,
  IconTag,
  IconInfo,
  IconDownload,
  IconRefresh,
  IconLayers,
} from '../components/icons';
import { Banner } from '../components/ui/banner';
import { cn } from '../../lib/utils';

/**
 * Coerces the loosely-typed `getLatestProjectScope` payload
 * (`Record<string, unknown> | null`) into `{ rooms: ScopeResponseRoom[] }`.
 * Returns null when there is no scope or no rooms array.
 *
 * @param raw - The raw scope payload.
 * @returns The coerced shape, or null.
 */
function coerceScope(
  raw: Record<string, unknown> | null,
): { rooms: ScopeResponseRoom[]; id: string | null; roof: Record<string, unknown> | null } | null {
  if (!raw) return null;
  // getLatestProjectScope returns a ScopeListItem: { id, scope_data: { rooms } }.
  // Read rooms from scope_data; tolerate a flattened shape (rooms at top) too.
  const sd = (raw as { scope_data?: unknown }).scope_data;
  const container = (sd && typeof sd === 'object' ? sd : raw) as {
    rooms?: unknown;
    metadata?: { roof?: unknown } | null;
  };
  const rooms = container.rooms;
  if (!Array.isArray(rooms)) return null;
  const id = ((raw as { id?: unknown }).id as string | undefined) ?? null;
  const roofRaw = container.metadata?.roof;
  const roof =
    roofRaw && typeof roofRaw === 'object' ? (roofRaw as Record<string, unknown>) : null;
  return { rooms: rooms as ScopeResponseRoom[], id, roof };
}

/** A line item plus its owning room, with a stable per-row key. */
interface FlatItem {
  key: string;
  room: string;
  item: ScopeResponseLineItem;
}

type ImportSummaryRow = import('../../types').ImportSummary;

/**
 * The flagship estimate-detail page.
 *
 * @returns The page element.
 */
export default function V2EstimateDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const getProjectById = useProjectStore((s) => s.getProjectById);

  const [project, setProject] = useState<Project | null>(null);
  const [scope, setScope] = useState<{
    rooms: ScopeResponseRoom[];
    id: string | null;
    roof: Record<string, unknown> | null;
  } | null>(null);
  const [notes, setNotes] = useState<FieldNote[]>([]);
  const [imports, setImports] = useState<ImportSummaryRow[]>([]);
  const [loading, setLoading] = useState(true);

  const [selected, setSelected] = useState<string>(SEL_ALL);
  const [search, setSearch] = useState('');
  const [whyOpen, setWhyOpen] = useState<string | null>(null);
  // Session-local CAT/SEL edits, keyed `${rowKey}:${field}`.
  // TODO: persist via BackendAPI.saveProjectScope (no per-line endpoint today).
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [editOpen, setEditOpen] = useState(false);
  // PRP-INTAKE-2 / PRP-WEB-1 W2.4: the tabs, linkable as ?tab= (the chat sends ?tab=intake).
  const [urlTab, selectTab] = useProjectPageTab();
  // PRP-WEB-1 W2.8: one Undo queue for the page, so a tab switch neither sends nor hides the toast.
  // Leaving the page (unmount, or another project in the same route) sends what was not undone.
  const deletes = useEstimateDeletes();
  const flushDeletes = deletes.flush;
  useEffect(() => () => flushDeletes(), [id, flushDeletes]);

  // Load project header + latest scope.
  useEffect(() => {
    let cancelled = false;
    if (!id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    // Imports load INSIDE the gated Promise.all (best-effort): previously they
    // resolved after `loading` flipped, so an import-only project flashed
    // "No scope yet … no imports" before the imports arrived.
    Promise.all([
      getProjectById(id),
      BackendAPI.getLatestProjectScope(id),
      BackendAPI.listImports(id).catch(() => []),
    ])
      .then(([proj, rawScope, rows]) => {
        if (cancelled) return;
        setProject(proj ?? null);
        setScope(coerceScope(rawScope));
        setImports(Array.isArray(rows) ? rows : []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    // Field notes/photos load independently — a notes failure must not blank
    // the estimate (and a note-only project must still show its captures).
    BackendAPI.getNotesForProject(id)
      .then((n) => {
        if (!cancelled) setNotes(Array.isArray(n) ? n : []);
      })
      .catch(() => {
        if (!cancelled) setNotes([]);
      });
    return () => {
      cancelled = true;
    };
  }, [id, getProjectById]);

  // ESX export of the persisted project scope (the "reopen project → export"
  // flow). Session-local CAT/SEL `overrides` are not yet persisted, so the
  // export reflects the last-saved scope (see TODO above; tracked as B-074).
  const { exportEsx, exporting, error: exportError } = useScopeEsxExport();
  const scopeId = scope?.id ?? null;
  const handleDownload = useCallback(async () => {
    if (!scopeId) return;
    await exportEsx({ scopeId, projectNameOverride: project?.owner_name || undefined });
  }, [scopeId, exportEsx, project]);

  const rooms = scope?.rooms ?? [];

  // Flatten all items with a stable key + owning room.
  const allItems = useMemo<FlatItem[]>(
    () =>
      rooms.flatMap((room, ri) =>
        room.items.map((item, ii) => ({
          key: `${ri}:${ii}`,
          room: room.room_name,
          item,
        })),
      ),
    [rooms],
  );

  // Resolve the effective CAT/SEL for a row through the override map.
  const resolveParts = (row: FlatItem) => {
    const base = splitXactimateCode(row.item.xactimate_code);
    const cat = overrides[`${row.key}:cat`] ?? base.cat;
    const sel = overrides[`${row.key}:sel`] ?? base.sel;
    return { ...base, cat, sel };
  };

  // Apply area-selection + text-search filters.
  const visibleItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allItems.filter((row) => {
      if (selected !== SEL_ALL && selected !== SEL_REVIEW && row.room !== selected) {
        return false;
      }
      if (!q) return true;
      const parts = resolveParts(row);
      const code = joinXactimateCode(parts.cat, parts.sel, parts.rest).toLowerCase();
      return (
        (row.item.description || '').toLowerCase().includes(q) || code.includes(q)
      );
    });
    // resolveParts depends on overrides; include it for search correctness.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allItems, selected, search, overrides]);

  // Group by surface / order by sequence via the IMPORTED helper.
  const groups = useMemo(
    () => groupItemsBySection(visibleItems.map((r) => r.item)),
    [visibleItems],
  );

  // Map each item back to its FlatItem (for stable keys + override addressing).
  const flatByItem = useMemo(() => {
    const m = new Map<ScopeResponseLineItem, FlatItem>();
    for (const row of visibleItems) m.set(row.item, row);
    return m;
  }, [visibleItems]);

  const areaLabel =
    selected === SEL_ALL
      ? 'All Items'
      : selected === SEL_REVIEW
        ? 'To Review'
        : selected;

  // PRP-FACTS-1: the newest chat estimate linked to this project, for its open items.
  const chatEstimateId = [...imports].filter((i) => i.parse_hash.startsWith('mcp-'))
    .sort((x, y) => String(y.created_at ?? '').localeCompare(String(x.created_at ?? '')))[0]?.parse_hash;
  const ownerName = projectLabel(project);
  // Graceful placeholders — Project carries no price-list / reference field.
  const priceListLabel = (project?.metadata?.price_list as string) || 'Default price list';
  const refTag = project?.claim_number || project?.id || '—';

  const hasScope = !!scope && rooms.length > 0;
  const tab = urlTab === 'scope' && !hasScope ? 'overview' : urlTab;
  // Back: the previous in-app page, or the dashboard when this page was opened directly.
  const goBack = () => (location.key === 'default' ? navigate('/') : navigate(-1));

  // ---- render states ----
  if (loading) {
    return (
      <div className="grid h-full place-items-center">
        <div className="text-[13px] text-[var(--tx-lo)]">Loading estimate…</div>
      </div>
    );
  }

  // Sub-header, details modal and the Estimate / Intake tabs: the same on every view.
  const header = (
    <>
      <SubHeader
        ownerName={ownerName}
        priceListLabel={priceListLabel}
        refTag={refTag}
        onBack={goBack}
        canDownload={!!scopeId}
        downloading={exporting}
        onDownload={handleDownload}
        onEditDetails={() => setEditOpen(true)}
      />
      {editOpen && project && (
        <ProjectDetailsModal
          project={project}
          onClose={() => setEditOpen(false)}
          onSaved={(updated) => {
            setProject(updated);
            setEditOpen(false);
          }}
        />
      )}
      <PageTabs tab={tab} onSelect={selectTab} showScope={hasScope} />
    </>
  );

  const body =
    !id ? null : tab === 'intake' ? (
      <IntakeTabPanel projectId={id} projectName={ownerName} />
    ) : tab === 'notes' ? (
      <NotesTabPanel projectId={id} notes={notes} />
    ) : tab === 'plan' ? (
      <PlanTabPanel projectId={id} notes={notes} />
    ) : tab === 'overview' ? (
      <ProjectOverview
        projectId={id}
        projectName={ownerName}
        notes={notes}
        imports={imports}
        chatEstimateId={chatEstimateId}
        onOpen={(url) => navigate(url)}
        onDeleted={(hash) => setImports((prev) => prev.filter((i) => i.parse_hash !== hash))}
        deletes={deletes}
      />
    ) : null;
  if (body || !hasScope) {
    return (
      <div className="flex h-full flex-col">
        {header}
        {body}
        {deletes.toast}
      </div>
    );
  }

  let ord = 0;

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex h-full flex-col" onClick={() => setWhyOpen(null)}>
        {header}
        {deletes.toast}

        {exportError && (
          <Banner tone="warn" className="m-3">
            Export failed: {exportError}
          </Banner>
        )}

        {/* master-detail */}
        <div className="flex min-h-0 flex-1">
          <div className="w-[268px] flex-none border-r border-[var(--bd-subtle)] bg-[var(--bg-1)]">
            <TreeRail
              rooms={rooms}
              totalCount={allItems.length}
              selected={selected}
              onSelect={setSelected}
              search={search}
              onSearchChange={setSearch}
            />
          </div>

          <div className="flex min-w-0 flex-1 flex-col">
            {/* grid header */}
            <div className="flex flex-none items-center gap-[18px] border-b border-[var(--bd-subtle)] px-[18px] pb-3 pt-[14px]">
              <div className="whitespace-nowrap text-[16px] font-[620] tracking-[-0.02em] text-[var(--tx-hi)]">
                {areaLabel}
              </div>
              <div className="flex gap-[22px]">
                <Stat label="Line items" value={String(visibleItems.length)} />
                <Stat label="RCV" value="—" />
              </div>
            </div>

            {/* grid */}
            <div className="flex-1 overflow-auto">
              {visibleItems.length === 0 ? (
                <EmptyState
                  icon={<IconLayers />}
                  title="No line items"
                  description={
                    search
                      ? 'No items match your search.'
                      : 'This area has no line items.'
                  }
                />
              ) : (
                <table className="w-full border-collapse text-[12.5px]" style={{ minWidth: 760 }}>
                  <thead>
                    <tr>
                      <Th className="w-[38px] text-right">#</Th>
                      <Th className="w-[66px]">CAT</Th>
                      <Th className="w-[78px]">SEL</Th>
                      <Th className="w-[44px]">Act</Th>
                      <Th>Description</Th>
                      <Th className="w-[92px] text-right">Qty</Th>
                      <Th className="w-[92px] text-right">Unit price</Th>
                      <Th className="w-[104px] text-right">Total</Th>
                      <Th className="w-[92px]">Conf.</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map((g) => (
                      <GroupBlock key={g.section} label={g.label} count={g.items.length}>
                        {g.items.map((item) => {
                          const row = flatByItem.get(item);
                          if (!row) return null;
                          const parts = resolveParts(row);
                          const sm = sourceMeta(item.source);
                          const sym = activitySymbol(item.activity);
                          const conf = CONF_META[item.confidence];
                          ord += 1;
                          const rowOrd = ord;
                          const isWhy = whyOpen === row.key;
                          return (
                            <Row
                              key={row.key}
                              row={row}
                              parts={parts}
                              sourceColor={sm.color}
                              sourceLabel={sm.label}
                              ord={rowOrd}
                              sym={sym}
                              confKind={conf?.kind ?? 'neutral'}
                              confLabel={conf?.label ?? item.confidence}
                              isWhy={isWhy}
                              onCommit={(field, value) =>
                                setOverrides((prev) => ({
                                  ...prev,
                                  [`${row.key}:${field}`]: value,
                                }))
                              }
                              onToggleWhy={() =>
                                setWhyOpen((cur) => (cur === row.key ? null : row.key))
                              }
                            />
                          );
                        })}
                      </GroupBlock>
                    ))}
                  </tbody>
                </table>
              )}

              {/* Roof scope (PRP-ROOF-2 S3): persisted at scope_data.metadata.roof. */}
              <RoofScopeSection roof={scope.roof} />

              {/* Read-only plan from the captured room geometry (PRP-SKETCH-FIELD-1). */}
              <FloorPlanView notes={notes} className="mx-[18px] my-4" />

              {/* source legend + pricing note */}
              <div className="flex flex-wrap items-center gap-4 border-t border-[var(--bd-subtle)] px-[18px] py-3">
                <span className="text-[10.5px] font-semibold tracking-[.06em] text-[var(--tx-faint)]">
                  SOURCE
                </span>
                {Object.entries(SOURCE_META).map(([k, v]) => (
                  <span
                    key={k}
                    className="inline-flex items-center gap-[6px] text-[11px] text-[var(--tx-lo)]"
                  >
                    <span
                      className="h-[7px] w-[7px] flex-none rounded-full"
                      style={{ background: v.color }}
                      aria-hidden="true"
                    />
                    {v.label}
                  </span>
                ))}
                <span className="ml-auto text-[11px] text-[var(--tx-faint)]">
                  Pricing &amp; totals are computed in Xactimate on export.
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}

/** Sub-header (52px): back, breadcrumb, price-list / reference pills, actions. */
function SubHeader({
  ownerName,
  priceListLabel,
  refTag,
  onBack,
  canDownload,
  downloading,
  onDownload,
  onEditDetails,
}: {
  ownerName: string;
  priceListLabel: string;
  refTag: string;
  onBack: () => void;
  canDownload?: boolean;
  downloading?: boolean;
  onDownload?: () => void;
  onEditDetails?: () => void;
}) {
  return (
    <div className="flex h-[52px] flex-none items-center gap-3 border-b border-[var(--bd-subtle)] bg-[var(--bg-1)] px-[18px]">
      <Button variant="ghost" size="sm" className="w-7 justify-center p-0" title="Back" onClick={onBack}>
        <IconChevl />
      </Button>
      <div className="flex min-w-0 items-center gap-2 text-[13.5px]">
        <Link to="/" className="text-[var(--tx-lo)] hover:text-[var(--accent-ink)] hover:underline" data-testid="crumb-estimates">
          Estimates
        </Link>
        <span className="text-[var(--tx-faint)]">/</span>
        <b className="truncate font-[600] text-[var(--tx-hi)]">{ownerName}</b>
      </div>
      <Pill kind="accent" className="max-md:hidden">
        <IconDollar size={11} />
        {priceListLabel}
      </Pill>
      <Pill kind="neutral" className="max-md:hidden">
        <IconTag size={11} />
        {refTag}
      </Pill>
      <div className="flex-1" />
      <Button variant="secondary" size="sm" onClick={onEditDetails} data-testid="edit-project-details">
        <IconInfo />
        Project details
      </Button>
      {canDownload && (
        <Button
          variant="primary"
          size="sm"
          onClick={onDownload}
          disabled={downloading}
          data-testid="estimate-download-esx"
        >
          {downloading ? <IconRefresh className="animate-spin" /> : <IconDownload />}
          {downloading ? 'Exporting…' : 'Download'}
        </Button>
      )}
    </div>
  );
}

/** A header stat (label over value). */
function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="whitespace-nowrap">
      <div className="text-[10px] font-[550] tracking-[.03em] text-[var(--tx-lo)]">{label}</div>
      <div className="font-[var(--font-mono)] text-[16px] font-[640] tabular-nums text-[var(--tx-hi)]">
        {value}
      </div>
    </div>
  );
}

/** Sticky table header cell. */
function Th({ children, className }: { children?: React.ReactNode; className?: string }) {
  return (
    <th
      className={cn(
        'sticky top-0 z-[2] h-9 border-b border-[var(--bd)] bg-[var(--bg-2)] px-3 text-left',
        'text-[11px] font-semibold tracking-[.02em] text-[var(--tx-lo)] whitespace-nowrap select-none',
        className,
      )}
    >
      {children}
    </th>
  );
}

/** A sticky group header row + its child rows. */
function GroupBlock({
  label,
  count,
  children,
}: {
  label: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <>
      <tr>
        <td colSpan={9} className="h-auto p-0">
          <div className="sticky top-9 z-[1] flex items-center gap-[9px] border-b border-[var(--bd-subtle)] bg-[var(--bg-1)] px-[14px] pb-[7px] pt-[9px]">
            <span className="text-[11px] font-[650] uppercase tracking-[.06em] text-[var(--tx-lo)]">
              {label}
            </span>
            <span className="h-px flex-1 bg-[var(--bd-subtle)]" />
            <span className="font-[var(--font-mono)] text-[11px] text-[var(--tx-faint)]">
              {count} items
            </span>
          </div>
        </td>
      </tr>
      {children}
    </>
  );
}

/** A single line-item row (+ optional inline "Why this code?" expander). */
function Row({
  row,
  parts,
  sourceColor,
  sourceLabel,
  ord,
  sym,
  confKind,
  confLabel,
  isWhy,
  onCommit,
  onToggleWhy,
}: {
  row: FlatItem;
  parts: { cat: string; sel: string; rest: string };
  sourceColor: string;
  sourceLabel: string;
  ord: number;
  sym: string;
  confKind: 'ok' | 'warn' | 'bad' | 'info' | 'neutral' | 'accent';
  confLabel: string;
  isWhy: boolean;
  onCommit: (field: 'cat' | 'sel', value: string) => void;
  onToggleWhy: () => void;
}) {
  const { item } = row;
  const why = item.reasoning ?? item.note ?? '—';
  const hasQty = item.quantity != null;

  return (
    <>
      <tr className="border-b border-[var(--bd-subtle)] hover:bg-[var(--bg-2)]">
        <td
          className="h-[var(--row-h)] py-0 pr-3 text-right font-[var(--font-mono)] tabular-nums text-[var(--tx-faint)]"
          style={{ borderLeft: `3px solid ${sourceColor}`, paddingLeft: 11 }}
          title={sourceLabel}
        >
          {ord}
        </td>
        <td className="h-[var(--row-h)] px-3">
          <CodeCell value={parts.cat} field="cat" onCommit={(v) => onCommit('cat', v)} />
        </td>
        <td className="h-[var(--row-h)] px-3">
          <CodeCell value={parts.sel} field="sel" onCommit={(v) => onCommit('sel', v)} />
        </td>
        <td className="h-[var(--row-h)] px-3">
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="grid h-[22px] w-[22px] cursor-default place-items-center rounded-[5px] border border-[var(--bd-subtle)] bg-[var(--bg-3)] font-[var(--font-mono)] text-[12px] text-[var(--tx-mid)]">
                {sym}
              </span>
            </TooltipTrigger>
            <TooltipContent>{ACT_LABEL[sym] ?? item.activity}</TooltipContent>
          </Tooltip>
        </td>
        <td
          className="h-[var(--row-h)] px-3 font-[530] text-[var(--tx-hi)]"
          style={{ maxWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {/* The text truncates, the info button never does: it sat after the text inside the
              clipped cell, so a long description pushed it out of sight (B-7385). */}
          <div className="flex min-w-0 items-center gap-[6px]">
            <span className="min-w-0 truncate">{item.description}</span>
            <button
              type="button"
              title="Why this code?"
              onClick={(e) => {
                e.stopPropagation();
                onToggleWhy();
              }}
              className="grid h-[18px] w-[18px] flex-none place-items-center rounded-[var(--r-xs)] hover:bg-[var(--bg-3)]"
            >
              <IconInfo
                size={13}
                className={isWhy ? 'text-[var(--accent-ink)]' : 'text-[var(--tx-faint)]'}
              />
            </button>
          </div>
        </td>
        <td className="h-[var(--row-h)] px-3 text-right font-[var(--font-mono)] tabular-nums">
          {hasQty ? (
            <>
              {item.quantity!.toLocaleString()}{' '}
              <span className="text-[11px] text-[var(--tx-faint)]">{item.unit}</span>
            </>
          ) : item.variable ? (
            <Pill kind="neutral" className="h-[18px] px-[6px] font-[var(--font-mono)]">
              <span title="Sketch variable">{item.variable}</span>
            </Pill>
          ) : (
            <span className="text-[var(--tx-faint)]">—</span>
          )}
        </td>
        <td className="h-[var(--row-h)] px-3 text-right text-[var(--tx-lo)]">—</td>
        <td className="h-[var(--row-h)] px-3 text-right text-[var(--tx-lo)]">—</td>
        <td className="h-[var(--row-h)] px-3">
          <Pill kind={confKind} dot={`var(--${confKind})`}>
            {confLabel}
          </Pill>
        </td>
      </tr>
      {isWhy && (
        <tr>
          <td colSpan={9} className="h-auto p-0">
            <div
              className="flex items-start gap-[14px] bg-[var(--bg-1)] px-4 py-[10px]"
              style={{ borderLeft: '3px solid var(--accent)' }}
            >
              <span className="inline-flex items-center gap-[6px] text-[11px] text-[var(--tx-lo)]">
                <span
                  className="h-[7px] w-[7px] flex-none rounded-full"
                  style={{ background: sourceColor }}
                  aria-hidden="true"
                />
                {sourceLabel}
              </span>
              <p className="m-0 max-w-[720px] text-[12px] leading-[1.5] text-[var(--tx-mid)]">
                {why}
              </p>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
